<template>
    <v-card class="combat-timeline mb-4" color="surface-variant" variant="outlined">
        <v-card-text class="pb-1">
            <div class="d-flex align-center justify-space-between mb-1">
                <div class="d-flex align-center gap-2">
                    <v-icon size="small" color="primary" icon="mdi-timeline-clock" class="mr-1" />
                    <span class="text-overline">Timeline Slice</span>
                    <v-chip v-if="!targetDead" size="x-small" color="warning" variant="tonal" class="ml-2" prepend-icon="mdi-timer-sand">
                        Awaiting Boss Death
                    </v-chip>
                    <v-chip v-else size="x-small" color="success" variant="tonal" class="ml-2" prepend-icon="mdi-check-circle">
                        {{ formatRelative(rangeValue[0]) }} → {{ formatRelative(rangeValue[1]) }}
                    </v-chip>
                </div>
                <v-btn v-if="targetDead && isSliced" size="x-small" variant="text" color="grey" @click="resetRange">
                    Reset
                </v-btn>
            </div>

            <!-- Render DPS Graph on top of the Timeline Slider -->
            <DpsGraph class="mb-4" :target-ids="targetIds" />

            <!-- Track container (slider + overlay) -->
            <div class="timeline-track-wrapper">
                <!-- Status 803 overlay bands (amber/orange) -->
                <div class="status-overlay" aria-hidden="true">
                    <div v-for="(band, i) in status803Bands" :key="i"
                        class="status-band"
                        :style="{ left: band.left + '%', width: band.width + '%' }"
                        title="Status 803 Active" />
                </div>

                <!-- Vuetify Range Slider -->
                <v-range-slider
                    v-model="rangeValue"
                    :min="0"
                    :max="totalDuration"
                    :step="3"
                    :disabled="!targetDead"
                    thumb-label="always"
                    thumb-size="12"
                    hide-details
                    color="primary"
                    track-color="grey-darken-3"
                    class="timeline-slider"
                    @end="onRangeCommit"
                >
                    <template #thumb-label="{ modelValue }">
                        {{ formatRelative(modelValue) }}
                    </template>
                </v-range-slider>
            </div>

            <!-- Time axis markers -->
            <div class="time-axis" v-if="totalDuration > 0">
                <span v-for="tick in axisTicks" :key="tick.value"
                    class="axis-tick"
                    :style="{ left: tick.pct + '%' }">
                    {{ tick.label }}
                </span>
            </div>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue';
import { formatDuration } from '@/util';
import DpsGraph from './DpsGraph.vue';

interface Interval {
    start: number;
    end: number;
}

export default defineComponent({
    name: 'CombatTimeline',
    components: {
        DpsGraph
    },

    props: {
        /** Absolute combat start timestamp (seconds) */
        combatStart: { type: Number, required: true },
        /** The selected target IDs to render the graph for */
        targetIds: { type: Array as PropType<string[]>, default: () => [] },
        /** Absolute combat end timestamp (seconds) */
        combatLast: { type: Number, required: true },
        /** Status 803 merged intervals (absolute seconds) */
        status803Intervals: { type: Array as PropType<Interval[]>, default: () => [] },
        /** Whether the selected boss entity is confirmed dead (disappearAt > 0) */
        targetDead: { type: Boolean, default: false },
    },

    emits: ['update:range'],

    setup(props, { emit }) {
        const totalDuration = computed(() => {
            const dur = props.combatLast - props.combatStart;
            return isFinite(dur) ? Math.max(3, Math.ceil(dur)) : 3;
        });

        // Internal slider value [from, to] in relative seconds from combatStart
        const rangeValue = ref<[number, number]>([0, 3]);

        // Initialise slider to full range on first mount (no emit — parent already has null/full-session).
        const initRange = () => {
            const dur = totalDuration.value;
            if (isFinite(dur) && dur > 0) rangeValue.value = [0, dur];
        };
        initRange();

        // When bounds change AFTER mount (new target selected, or live session grows):
        // - If the slider is at the full range, extend the right thumb to match and keep null.
        // - If the user has an active slice, leave it alone.
        watch(totalDuration, (newDur, oldDur) => {
            if (!isFinite(newDur) || newDur <= 0) return;
            if (oldDur === undefined) return; // first call handled by initRange above
            const [lo, hi] = rangeValue.value;
            const wasAtEnd = oldDur !== undefined && hi >= oldDur - STEP;
            if (lo <= STEP && wasAtEnd) {
                // Slider was at full range — extend to new duration, keep parent null (no fetch)
                rangeValue.value = [0, newDur];
                // Only emit null if this is a genuine target-selection change (large jump)
                // not just a small live-session tick extension.
                if (Math.abs(newDur - (oldDur ?? newDur)) > STEP * 2) {
                    emit('update:range', null);
                }
            }
            // If user has a custom slice active, do nothing
        });

        // Whether the current selection is a partial slice.
        // Use a tolerance of 1 step (3s) to handle floating point drift
        // and live-session updates that slightly shift totalDuration.
        const STEP = 3;
        const isSliced = computed(() =>
            isFinite(totalDuration.value) &&
            (rangeValue.value[0] > STEP || rangeValue.value[1] < totalDuration.value - STEP)
        );

        const resetRange = () => {
            const dur = totalDuration.value;
            rangeValue.value = [0, isFinite(dur) ? dur : 3];
            emit('update:range', null);
        };

        // Emit absolute timestamps when user finishes dragging
        const onRangeCommit = () => {
            const dur = totalDuration.value;
            if (!isFinite(dur) || dur <= 0) return; // not ready yet
            const [from, to] = rangeValue.value;
            if (!isSliced.value) {
                emit('update:range', null);
            } else {
                // To accurately capture all events within the relative slider ticks,
                // we treat the floor of combatStart as T=0 and pad the end time with .999
                // to include trailing millisecond events for the final selected second.
                const baseStart = Math.floor(props.combatStart);
                emit('update:range', {
                    from: baseStart + from,
                    to: baseStart + to + 0.999,
                });
            }
        };

        // Format relative offset to standard duration format
        const formatRelative = (relSec: number) => {
            return formatDuration(relSec);
        };

        // Convert status 803 intervals to % bands on the track
        const status803Bands = computed(() => {
            const dur = totalDuration.value;
            if (!props.status803Intervals?.length || !isFinite(dur) || dur <= 0) return [];
            
            const base = isFinite(props.combatStart) ? props.combatStart : 0;

            return props.status803Intervals.map(iv => {
                const relStart = Math.max(0, iv.start - base);
                const relEnd = Math.min(dur, iv.end - base);
                if (!isFinite(relStart) || !isFinite(relEnd) || relEnd <= relStart) return null;
                return {
                    left: (relStart / dur) * 100,
                    width: ((relEnd - relStart) / dur) * 100,
                };
            }).filter(Boolean) as { left: number; width: number }[];
        });

        // Axis tick marks every ~1 minute (or every 30s for short fights)
        const axisTicks = computed(() => {
            const dur = totalDuration.value;
            if (dur <= 0) return [];
            
            // Limit number of ticks to avoid UI clutter
            const step = dur <= 120 ? 30 : 60;
            const ticks = [];
            for (let t = 0; t <= dur; t += step) {
                ticks.push({
                    value: t,
                    pct: (t / dur) * 100,
                    label: formatRelative(t),
                });
                if (ticks.length > 50) break; // Safety break
            }
            return ticks;
        });

        return {
            rangeValue,
            totalDuration,
            isSliced,
            status803Bands,
            axisTicks,
            formatRelative,
            resetRange,
            onRangeCommit,
        };
    }
});
</script>

<style scoped>
.combat-timeline {
    position: relative;
}

.timeline-track-wrapper {
    position: relative;
    padding-top: 4px;
}

/* Status 803 amber overlay bands */
.status-overlay {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    right: 0;
    height: 6px;
    pointer-events: none;
    z-index: 1;
    margin: 0 10px; /* align with slider track padding */
}

.status-band {
    position: absolute;
    height: 100%;
    background: rgba(255, 152, 0, 0.55);
    border-radius: 3px;
}

/* Time axis */
.time-axis {
    position: relative;
    height: 16px;
    margin: 0 10px;
    margin-top: -4px;
}

.axis-tick {
    position: absolute;
    transform: translateX(-50%);
    font-size: 0.65rem;
    color: #9e9e9e;
    white-space: nowrap;
}

/* Bump z-index so slider thumbs render above the overlay bands */
:deep(.v-slider-thumb) {
    z-index: 2;
}
</style>
